import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '@/context/StoreContext';
import { Seo } from '@/components/Seo';
import { Minus, Plus, Trash2, ArrowRight } from 'lucide-react';

export default function Cart() {
  const { t, lang, cart, updateQty, removeFromCart, formatPrice, cartSubtotal, hydrateCart } = useStore();
  const navigate = useNavigate();
  useEffect(() => { hydrateCart(); }, []); // eslint-disable-line
  const vatRate = 20;
  const vatIncluded = cartSubtotal * vatRate / (100 + vatRate);
  const seo = <Seo title={t.cart.title} description={lang === 'fr' ? 'Votre panier NICEWINE.' : 'Your NICEWINE cart.'} path="/cart" lang={lang} noindex />;

  if (cart.length === 0) {
    return (
      <div className="nw-container py-32 text-center">
        {seo}
        <h1 className="font-serif text-4xl text-nw-ivory">{t.cart.title}</h1>
        <p className="mt-4 text-nw-warmgrey">{t.cart.empty}</p>
        <Link to="/collections" className="nw-btn-gold mt-8">{t.cart.continue}</Link>
      </div>
    );
  }

  return (
    <div className="bg-nw-black">
      {seo}
      <div className="nw-container py-14">
        <h1 className="font-serif text-4xl text-nw-ivory mb-10">{t.cart.title}</h1>
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-12">
          <div data-testid="cart-items-table">
            {cart.map((item) => (
              <div key={item.product_id} data-testid={`cart-item-${item.reference_id}`} className="flex gap-5 py-6 border-b border-nw-gold/15">
                <Link to={`/product/${item.product_id}`} className="w-24 h-32 shrink-0 bg-nw-espresso overflow-hidden border border-nw-gold/15">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                </Link>
                <div className="flex-1 flex flex-col justify-between">
                  <div>
                    <Link to={`/product/${item.product_id}`} className="font-serif text-lg text-nw-ivory hover:text-nw-gold transition-colors">{item.name}</Link>
                    <div className="text-[11px] tracking-wide uppercase text-nw-warmgrey mt-1">{item.reference_id} · {item.format}</div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-nw-gold/25 rounded-[3px] h-10">
                      <button data-testid={`cart-dec-${item.reference_id}`} onClick={() => updateQty(item.product_id, item.quantity - 1)} className="w-9 h-full text-nw-ivory hover:text-nw-gold flex items-center justify-center"><Minus size={14} /></button>
                      <span className="w-9 text-center text-sm text-nw-ivory">{item.quantity}</span>
                      <button data-testid={`cart-inc-${item.reference_id}`} onClick={() => updateQty(item.product_id, item.quantity + 1)} className="w-9 h-full text-nw-ivory hover:text-nw-gold flex items-center justify-center"><Plus size={14} /></button>
                    </div>
                    <button data-testid={`cart-remove-${item.reference_id}`} onClick={() => removeFromCart(item.product_id)} className="text-nw-warmgrey hover:text-nw-gold transition-colors flex items-center gap-1 text-xs"><Trash2 size={14} /> {t.cart.remove}</button>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-nw-gold">{formatPrice(item.price_eur * item.quantity)}</div>
                  <div className="text-[11px] text-nw-warmgrey mt-1">{formatPrice(item.price_eur)}</div>
                </div>
              </div>
            ))}
            <Link to="/collections" className="inline-block mt-6 text-[11px] tracking-luxe uppercase nw-link">← {t.cart.continue}</Link>
          </div>
          <div className="mt-10 lg:mt-0">
            <div className="bg-nw-espresso border border-nw-gold/20 p-7 lg:sticky lg:top-[92px]">
              <h2 className="nw-eyebrow mb-5">{t.checkout.summary}</h2>
              <div className="flex justify-between text-sm py-2"><span className="text-nw-warmgrey">{t.cart.subtotal}</span><span className="text-nw-ivory">{formatPrice(cartSubtotal)}</span></div>
              <div className="flex justify-between text-[12px] py-1"><span className="text-nw-warmgrey">{t.cart.vatOf} ({vatRate}%)</span><span className="text-nw-warmgrey">{formatPrice(vatIncluded)}</span></div>
              <div className="flex justify-between text-sm py-2 border-t border-nw-gold/15 mt-2"><span className="text-nw-warmgrey">{t.cart.shipping}</span><span className="text-nw-warmgrey text-xs">{t.checkout.options}</span></div>
              <div className="flex justify-between items-baseline mt-5 pt-5 border-t border-nw-gold/25">
                <span className="text-[11px] tracking-luxe uppercase text-nw-warmgrey">{t.cart.total}</span>
                <span className="font-serif text-2xl text-nw-gold">{formatPrice(cartSubtotal)}</span>
              </div>
              <button data-testid="cart-checkout-button" onClick={() => navigate('/checkout')} className="nw-btn-gold w-full mt-6 h-12">{t.cart.proceed} <ArrowRight size={16} /></button>
              <p className="mt-3 text-[11px] text-nw-warmgrey/70 text-center">{t.common.vat}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
